import { createContext, useContext, useState, useCallback } from "react";

const BranchContext = createContext(null);

export const ALL_BRANCHES_OPTION = {
    code: 0,
    id: 0,
    branchoffice_id: 0,
    name: "Todas las sucursales",
    branchoffice_name: "Todas las sucursales",
};

//localStorage["branchoffice"] is the scheme most of the app already reads/writes
//(via ModalBranchofficeSelect); this context becomes the single source of truth for it.
const readStoredBranch = () => {
    try {
        const stored = localStorage.getItem("branchoffice");
        if (stored === null || stored === undefined || stored === "" || stored === "0") {
            return ALL_BRANCHES_OPTION;
        }
        const parsed = JSON.parse(stored);
        if (!parsed || typeof parsed !== "object") return ALL_BRANCHES_OPTION;
        return parsed;
    } catch (err) {
        return ALL_BRANCHES_OPTION;
    }
};

const getUserBranchoffices = () => {
    try {
        const user = JSON.parse(localStorage.getItem("user"));
        return Array.isArray(user?.branchoffices) ? user.branchoffices : [];
    } catch (err) {
        return [];
    }
};

//Different pages only know one half of a branch's identity (some track `code`, ProductPanel
//tracks the internal branchoffice_id) — this resolves whichever half was passed against the
//user's full branchoffice list so every consumer always gets both, regardless of who set it.
const resolveBranch = (branch) => {
    const branchoffices = getUserBranchoffices();
    const rawCode = branch?.code;
    const rawId = branch?.branchoffice_id ?? branch?.id;

    const match = branchoffices.find((b) =>
        (rawCode !== undefined && Number(rawCode) !== 0 && String(b.code) === String(rawCode)) ||
        (rawId !== undefined && Number(rawId) !== 0 && String(b.branchoffice_id ?? b.id) === String(rawId))
    );

    const source = match || branch || {};
    const code = source.code ?? 0;
    const id = source.branchoffice_id ?? source.id ?? 0;

    if (!Number(code) && !Number(id)) return ALL_BRANCHES_OPTION;

    return {
        code,
        id,
        branchoffice_id: id,
        name: source.branchoffice_name || source.name || "",
        branchoffice_name: source.branchoffice_name || source.name || "",
    };
};

const sameBranch = (a, b) => a === b || (
    a.code === b.code && a.branchoffice_id === b.branchoffice_id && a.name === b.name
);

export function BranchProvider({ children }) {
    const [selectedBranch, setSelectedBranchState] = useState(readStoredBranch);

    const setSelectedBranch = useCallback((branch) => {
        const normalized = !branch || branch === 0 ? ALL_BRANCHES_OPTION : resolveBranch(branch);

        //resolveBranch always builds a fresh object; bailing out here when nothing actually
        //changed keeps the reference stable so effects keyed on selectedBranch (some of which
        //re-call setSelectedBranch with its own current value) don't loop forever.
        setSelectedBranchState((prev) => (sameBranch(prev, normalized) ? prev : normalized));
        localStorage.setItem("branchoffice", normalized.code === 0 ? "0" : JSON.stringify(normalized));
        //Kept in sync for the code paths (socket event matching) that read this raw-code key directly
        localStorage.setItem("branchoffice_selected", normalized.code);
    }, []);

    return (
        <BranchContext.Provider value={{ selectedBranch, setSelectedBranch }}>
            {children}
        </BranchContext.Provider>
    );
}

export const useSelectedBranch = () => {
    const ctx = useContext(BranchContext);
    if (!ctx) throw new Error("useSelectedBranch must be used within a BranchProvider");
    return ctx;
};

export default BranchContext;
